<template>
  <div class="mb-4 h-fit space-y-6 lg:col-span-2 lg:mb-0">
    <LazyMarketingContentVideo
      v-if="hasYoutubeUrl"
      class="w-full"
      :blok="blokWithYoutubeUrl"
    />
    <UCard
      v-else
      :ui="{
        root: 'bg-neutral-900/40 backdrop-blur-lg overflow-hidden',
        body: 'p-0 sm:p-0 overflow-hidden',
      }"
    >
      <NuxtImg
        provider="storyblok"
        :modifiers="{ smart: true }"
        class="h-full w-full"
        sizes="xs:100svw sm:100svw md:100svw lg:40svw xl:40svw xxl:30svw"
        :src="blok.feature_image?.filename ?? ''"
        :alt="blok.feature_image?.alt ?? ''"
        loading="lazy"
        placeholder
      />
      <template v-if="blok.activity_types.includes('corporate')" #footer>
        <div class="flex items-center justify-between space-x-4">
          <div>
            <h3 class="text-xl font-extrabold text-white">
              Corporate Activity
            </h3>
            <p class="text-base font-medium text-neutral-400">
              Available with our corporate team building events.
            </p>
          </div>
          <UIcon
            name="i-lucide-briefcase-business"
            class="size-6 shrink-0 text-yellow-400"
          />
        </div>
      </template>
    </UCard>
    <UCard
      v-if="!props.blok.activity_types.includes('corporate')"
      :ui="{
        root: 'overflow-hidden bg-neutral-900/40',
        header: 'p-0 sm:p-0',
        body: 'p-0 sm:p-0',
        footer: 'p-2 sm:p-4',
      }"
    >
      <template #header>
        <div
          class="grid grid-cols-2 divide-x divide-neutral-800 text-sm font-medium"
        >
          <div
            class="flex items-center justify-center space-x-2 border-b border-neutral-800 p-2.5"
          >
            <div class="shrink-0">
              <UIcon
                v-for="i in 5"
                :key="i"
                name="i-heroicons-star-solid"
                class="size-5 text-yellow-400"
              />
            </div>
            <span class="-mt-1"> {{ blok.review_count }}+ </span>
          </div>
          <div
            class="flex items-center justify-center space-x-2 border-b border-neutral-800 p-2.5"
          >
            <UIcon
              name="i-lucide-pound-sterling"
              class="size-5 shrink-0 text-yellow-400"
            /><span
              ><span v-if="(blok.products?.length ?? 0) > 1">Starting At </span
              >£{{
                formatPrice(Number(blok.products?.[0]?.price ?? '0'))
              }}</span
            >
          </div>
          <div
            class="flex items-center justify-center space-x-2 border-b border-neutral-800 p-2.5"
          >
            <UIcon
              name="i-lucide-cake"
              class="size-5 shrink-0 text-yellow-400"
            /><span>Ages: {{ blok.age_range }}</span>
          </div>
          <div
            class="flex items-center justify-center space-x-2 border-b border-neutral-800 p-2.5"
          >
            <UIcon
              name="i-lucide-users"
              class="size-5 shrink-0 text-yellow-400"
            /><span>{{ blok.group_size }} People</span>
          </div>
          <UTooltip
            :text="locationNames"
            :content="{
              align: 'center',
              side: 'bottom',
              sideOffset: 8,
            }"
          >
            <div class="flex items-center justify-center space-x-2 p-2.5">
              <UIcon
                name="i-lucide-map-pin"
                class="size-5 shrink-0 text-yellow-400"
              /><span
                >{{ blok.locations?.length ?? 0 }}
                {{
                  (blok.locations?.length ?? 0) === 1 ? 'Location' : 'Locations'
                }}</span
              >
            </div>
          </UTooltip>
          <UTooltip
            :text="productNames"
            :content="{
              align: 'center',
              side: 'bottom',
              sideOffset: 8,
            }"
          >
            <div class="flex items-center justify-center space-x-2 p-2.5">
              <UIcon
                name="i-lucide-swatch-book"
                class="size-5 shrink-0 text-yellow-400"
              /><span
                >{{ (blok.products?.length ?? 0) + packagesCount }}
                {{
                  (blok.products?.length ?? 0) + packagesCount === 1
                    ? 'Option'
                    : 'Options'
                }}</span
              >
            </div>
          </UTooltip>
          <div
            class="flex items-center justify-center space-x-2 border-t border-neutral-800 p-2.5"
          >
            <UIcon
              name="i-lucide-gift"
              class="size-5 shrink-0 text-yellow-400"
            /><span
              >{{ giftsCount }}
              {{ giftsCount === 1 ? 'Gift Voucher' : 'Gift Vouchers' }}</span
            >
          </div>
          <div
            class="flex items-center justify-center space-x-2 border-t border-neutral-800 p-2.5"
          >
            <UIcon
              name="i-lucide-calendar-days"
              class="size-5 shrink-0 text-yellow-400"
            /><span>
              {{ eventsCount }}
              {{ eventsCount === 1 ? 'Event' : 'Events' }}</span
            >
          </div>
        </div>
      </template>
      <div v-if="show" class="relative isolate h-full w-full p-2 sm:p-4">
        <div
          class="absolute inset-0 h-full w-full animate-pulse opacity-20"
          style="animation-duration: 5s"
        >
          <BrandTopography />
        </div>
        <div
          class="absolute inset-0 z-[1] h-full w-full bg-gradient-to-b from-transparent via-neutral-950/50 to-neutral-950"
        />
        <div
          class="relative z-[2] flex items-center justify-between font-extrabold text-yellow-400 uppercase"
        >
          <div class="flex items-center space-x-4">
            <span>Recently Booked</span>
            <UButton
              variant="subtle"
              size="xs"
              class="cursor-pointer"
              @click="updateQueryParams"
              >BOOK NOW</UButton
            >
          </div>
          <UIcon name="i-lucide-flame" class="size-6 text-yellow-400" />
        </div>
      </div>
      <template v-if="!show" #footer>
        <UButton
          variant="subtle"
          size="xl"
          class="w-full cursor-pointer justify-center text-center"
          @click="updateQueryParams"
        >
          Book Now
        </UButton>
      </template>
    </UCard>
    <div
      v-if="blok.faqs && blok.faqs.length !== 0"
      class="mt-6 hidden lg:block"
    >
      <LazyMarketingContentFaqs :blok="blok" :faqs="blok.faqs" />
    </div>
  </div>
</template>

<script setup lang="ts">
import type { PropType } from 'vue';
import type { ActivityStoryblok } from '~/types/component-types-sb';

const route = useRoute();
const router = useRouter();
const { formatPrice } = useFormatPrice();

const props = defineProps({
  blok: {
    type: Object as PropType<ActivityStoryblok>,
    required: true,
  },
  giftsCount: {
    type: Number,
    default: 0,
  },
  eventsCount: {
    type: Number,
    default: 0,
  },
  packagesCount: {
    type: Number,
    default: 0,
  },
});

const show = ref(false);

const hasYoutubeUrl = computed(() => !!props.blok.youtube_url?.url);
const blokWithYoutubeUrl = computed(
  () =>
    props.blok as ActivityStoryblok & {
      youtube_url: NonNullable<ActivityStoryblok['youtube_url']>;
    }
);

onMounted(() => {
  // Generate a random number between 1 and 5
  const random = Math.floor(Math.random() * 5) + 1;

  // Show the component only if the random number is 1
  show.value = random === 1;
});

const productNames = computed(() => {
  return props.blok.products?.length
    ? props.blok.products.map((product) => product.name).join(', ')
    : 'No products available';
});

const locationNames = computed(() => {
  return props.blok.locations?.length
    ? props.blok.locations.map((location) => location.content.town).join(', ')
    : 'No locations available';
});

const updateQueryParams = () => {
  router.push({
    query: {
      ...route.query,
      tab: 'availability',
    },
  });
};
</script>
